import { useEffect, useMemo, useState } from "react";
import { Divider, Dropdown, Modal, Space, Typography } from "antd";
import Title from "antd/es/typography/Title";
import Paragraph from "antd/es/typography/Paragraph";
import { TbDotsVertical } from "react-icons/tb";
import { PiRepeat } from "react-icons/pi";
import { MdOutlineDelete } from "react-icons/md";
import { useApiCall } from "../../services/api";
import SearchInput from "../Forms/SearchInput";
import PrimaryButton from "../Forms/PrimaryButton";
import ConfirmModal from "./ConfirmModal";
import ManageAgentsAccess from "./ManageAgentsAccess";
import { RiDeleteBin6Line } from "react-icons/ri";

export default function ManageAgents({ open, onClose, data }) {
  const { postApi, loading } = useApiCall();
  const [allCustomers, setAllCustomers] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [modalAction, setModalAction] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [isAccessOpen, setIsAccessOpen] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [newCustomers, setNewCustomers] = useState([]);
  const [agentClientsMap, setAgentClientsMap] = useState({});

  const agents = useMemo(() => {
    return (allCustomers || []).filter((item) => item.is_agent);
  }, [allCustomers]);

  const filteredAgents = useMemo(() => {
    const value = searchText.toLowerCase().trim();
    if (!value) return agents;
    return agents.filter((agent) => (agent.company_name || "").toLowerCase().includes(value));
  }, [agents, searchText]);

  const existingAgentCustomerIds = useMemo(() => {
    return (allCustomers || []).filter((item) => item.is_agent).map((item) => item.customer_id || item.id);
  }, [allCustomers]);

  const selectedAgentCustomerIds = useMemo(() => {
    if (!selectedAgent) return [];
    const agentId = selectedAgent?.customer_id || selectedAgent?.id;
    return agentClientsMap[agentId] || [];
  }, [selectedAgent, agentClientsMap]);

  const reassignOptions = useMemo(() => {
    if (!selectedAgent) return [];

    const selectedAgentId = selectedAgent?.customer_id;

    return (allCustomers || []).filter((item) => {
      const itemId = item?.customer_id || item?.id;
      return !item.is_agent && itemId !== selectedAgentId;
    });
  }, [allCustomers, selectedAgent]);

  const fetchAllCustomers = async () => {
    try {
      const response = await postApi("sales/assign-customer-list", { user_id: data?.user_id });
      const list = response?.data || [];
      setAllCustomers(list);

      const map = {};
      list.forEach((customer) => {
        const agentId = customer.agent_data?.customer_id;
        if (!agentId) return;
        if (!map[agentId]) {
          map[agentId] = [];
        }
        map[agentId].push(customer.customer_id || customer.id);
      });
      setAgentClientsMap(map);
    } catch (err) {
      console.error("Error fetching customers:", err);
      setAllCustomers([]);
      setAgentClientsMap({});
    }
  };

  useEffect(() => {
    if (open) {
      fetchAllCustomers();
      setSearchText("");
      setSelectedAgent(null);
      setModalAction(null);
      setIsAccessOpen(false);
      setCustomers([]);
      setNewCustomers([]);
    }
  }, [open]);

  const openAddAgentModal = () => {
    setSelectedAgent(null);
    setCustomers([]);
    setIsAccessOpen(true);
  };

  const handleReassign = (agent) => {
    setSelectedAgent(agent);
    setCustomers(agentClientsMap[agent?.customer_id || agent?.id || agent?.agent_id]?.filter((a) => a !== selectedAgent?.customer_id) || []);
    setIsAccessOpen(true);
  };

  const handleDelete = (agent) => {
    setSelectedAgent(agent);
    setModalAction("DELETE_AGENT");
  };

  const handleDeleteConfirm = async () => {
    if (!selectedAgent) return;

    try {
      const response = await postApi("sales/agent/make-agent-remove", { customer_id: selectedAgent?.customer_id }, true);
      if (response?.status) {
        setModalAction(null);
        setSelectedAgent(null);
        fetchAllCustomers();
      }
    } catch (err) {
      console.error("Error deleting agent:", err);
    }
  };

  const handleAccessSubmit = async (selectedCustomerIds = []) => {
    try {
      const endpoint = selectedAgent ? "sales/agent/assign-agent-customer" : "sales/agent/make-agent";
      const payload = selectedAgent
        ? {
            agent_assign_customer_id: selectedAgent?.customer_id,
            customer_id: selectedCustomerIds,
            remove_customer_id: selectedAgentCustomerIds.filter((id) => !selectedCustomerIds.includes(id)),
          }
        : {
            customer_id: selectedCustomerIds,
          };

      const response = await postApi(endpoint, payload, true);
      if (response?.status) {
        setIsAccessOpen(false);
        setSelectedAgent(null);
        setCustomers([]);
        setNewCustomers([]);
        fetchAllCustomers();
      }
    } catch (err) {
      console.error("Error saving agents:", err);
    }
  };

  const getDropdownMenuItems = (item) => ({
    items: [
      {
        key: "reassign",
        label: (
          <Space className="text-[#A33434] font-semibold">
            <div className="bg-[#A33434] p-1.5 flex items-center justify-center rounded-full text-[#ffffff]">
              <PiRepeat />
            </div>
            Update Clients
          </Space>
        ),
      },
      {
        key: "delete",
        label: (
          <Space className="text-[#A33434] font-semibold">
            <div className="bg-[#A33434] p-1.5 flex items-center justify-center rounded-full text-[#ffffff]">
              <RiDeleteBin6Line />
            </div>
            Delete Agent
          </Space>
        ),
      },
    ],
    onClick: ({ key, domEvent }) => {
      domEvent.stopPropagation();

      switch (key) {
        case "reassign":
          handleReassign(item);
          break;
        case "delete":
          handleDelete(item);
          break;
        default:
          break;
      }
    },
  });

  return (
    <>
      <Modal className="form-css" open={open} footer={null} onCancel={onClose}>
        <div className="flex justify-between items-center">
          <Title level={2} className="text-3xl font-semibold text-gray-800 font-abhaya m-0">
            Manage Agents
          </Title>
        </div>

        <Paragraph className="input-css !mb-2" level={5}>
          Salesperson - <strong>{data?.full_name}</strong>
        </Paragraph>

        <div className="flex items-center gap-4 mb-4">
          <div className="flex-1">
            <SearchInput value={searchText} onChange={(e) => setSearchText(e.target.value)} placeholder="Search for agents" />
          </div>
          <PrimaryButton btnType="secondary" text="Add Agent" onClick={openAddAgentModal} />
        </div>

        <div className={`flex justify-between items-center mb-2 px-4`}>
          <span className="w-[8%] text-left font-medium text-gray-700">#</span>
          <span className="w-[62%] text-gray-900 text-left">Agent Name</span>
          <span className="w-[30%] text-gray-900 text-center">Actions</span>
        </div>
        <Divider className="my-3" />

        <div className="space-y-3">
          {filteredAgents.length > 0 ? (
            filteredAgents.map((item, index) => (
              <div key={item?.customer_id || item?.id || index} className={`flex justify-between items-center px-4 py-2 rounded-md bg-white `}>
                <div className="w-full text-left flex items-center">
                  <span className="font-medium text-gray-700 w-[8%]">{index + 1}</span>
                  <span className="text-gray-900 text-left w-[62%]">{item.company_name}</span>
                  <div className="w-[30%] flex justify-center">
                    <Dropdown menu={getDropdownMenuItems(item)} trigger={["click"]} overlayClassName="custom-dropdown-sales-menu">
                      <Typography.Link
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                      >
                        <Space>
                          <TbDotsVertical className="text-red-700" size={18} />
                        </Space>
                      </Typography.Link>
                    </Dropdown>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="flex justify-center items-center py-10">
              <span className="text-gray-500">Sorry, no agents found</span>
            </div>
          )}
        </div>
      </Modal>

      <ConfirmModal
        actionKey={modalAction === "DELETE_AGENT" ? "GENERIC_CONFIRM" : null}
        record={selectedAgent}
        open={modalAction === "DELETE_AGENT"}
        onCancel={() => {
          setModalAction(null);
          setSelectedAgent(null);
        }}
        onConfirm={handleDeleteConfirm}
        loading={loading}
        title="Delete Agent"
        description={
          selectedAgent ? (
            <>
              Do you want to delete <span className="font-bold">{selectedAgent.company_name}</span> from <span className="font-bold">agents</span>?
            </>
          ) : (
            ""
          )
        }
        confirmText="Delete"
      />

      <Modal className="form-css-2" open={isAccessOpen} footer={null} onCancel={() => setIsAccessOpen(false)} width={600} destroyOnHidden>
        <ManageAgentsAccess
          open={isAccessOpen}
          type={selectedAgent ? "UPDATE_CLIENTS" : "ADD_AGENT"}
          title={selectedAgent ? "Update Clients" : "Add Agent"}
          description={
            selectedAgent ? (
              <Paragraph className="input-css !my-2" level={5}>
                Assigned Salesperson - <strong>{data?.full_name}</strong>
              </Paragraph>
            ) : (
              " "
            )
          }
          plainOptions={selectedAgent ? reassignOptions : allCustomers.filter((item) => !item.is_agent && item.agent_data == null)}
          setNewCustomers={setNewCustomers}
          setCustomers={setCustomers}
          customers={customers}
          handleSubmit={handleAccessSubmit}
          submitText={selectedAgent ? "Update" : "Add"}
          loading={loading}
          searchPlaceholder={"Search for clients"}
          selectedAgent={selectedAgent}
          initialSelectedIds={selectedAgent ? selectedAgentCustomerIds : []}
        />
      </Modal>
    </>
  );
}
